import {
  getAvailableStartingPricing,
  getLink,
  getPricingDuration,
  getPropertyFilter,
} from "@Pages/Search/components/SearchListItem/helper";
import { getSearchLink } from "@Pages/Search/data/helper";
import getFeaturedImage from "@Utils/getFeaturedImage";
import getImagePath from "@Utils/getImagePath";
import { MouseEventHandler, useRef, useState, useEffect } from "react";
import styles from "./CompactInventoryCard.module.scss";
import Chevron from "@Icons/Chevron";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES, PAGE } from "constants/index";
import Carousel from "@Components/Carousel";
import Image from "@Components/Image";
import { getLocation } from "@Pages/PropertyPage/data/helper";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import CarouselIndicator from "@Components/Carousel/components/CarouselIndicator";
import { useHistory } from "hooks/useHistory";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import DistanceScroll from "@Pages/PropertyPage/components/PropertyDetailsSection/components/DistanceScroll";
import ShortlistButton from "@Pages/Search/components/SearchListItem/components/ShortListButton";
// import TranslucentIconTag from "@Pages/Search/components/SearchListItem/components/TranslucentTag";
// import VideoIcon from "@Icons/VideoIcon";
import TrustPilotBadge from "@Components/TrustPilotBadge";
import customHistoryPush from "@Utils/customHistoryPush";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import CustomLink from "@Components/CustomLink";
import useWhitelabel from "hooks/useWhitelabel";
import PropertyTags from "@Pages/Search/components/SearchListItem/components/PropertyTags";
import { useAmberSales } from "hooks/useAmberSales";
import Button from "@Components/Button";
import { AppState } from "@Store/index";
import useIsHappyFlow from "hooks/useIsHappyFlow";
import useIsHappyFlowBookForm from "hooks/useIsHappyFlowBookForm";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import isPaymentActive from "@Utils/isPaymentActive";
import { runTasks } from "@Utils/performanceUtils";
import navigateTo from "@Utils/webApputils/navigateTo";
import config from "@Config/index";
import { useIsIframe } from "hooks/useIsIframe";
import { appendIframeSourceToUrl } from "@Utils/iframeContext";

const CompactInventoryCard = ({
  item,
  parentCanonicalName = null,
  link: customLink = null,
  gtagEventCategory = "",
  gtagEventAction = "",
  isDesktop = false,
  showIcons = false,
  showDistanceRails = false,
  dataTestId = "",
  showHappyFlowCTA = false,
  screenName = "",
  className,
}: any) => {
  const { isDemoSubdomain, partner_uuid, isWhitelabel } = useWhitelabel();
  const { isIframe, iframeSource } = useIsIframe();
  const rawLink =
    customLink ||
    (parentCanonicalName
      ? getSearchLink(parentCanonicalName, isDemoSubdomain ? { partner_id: partner_uuid } : {})
      : getLink(item, isDemoSubdomain ? { partner_id: partner_uuid } : {}));
  const link = appendIframeSourceToUrl(rawLink, isIframe, iframeSource);
  const location = getLocation(item?.location);
  const history = useHistory();
  const dispatch = useDispatch();

  const pricingPrice = getAvailableStartingPricing(item?.pricing);
  const pricingDuration: any = getPricingDuration(item?.pricing?.duration);
  let featuredImage = getFeaturedImage(item?.images || []);
  const imageWrapperRef = useRef(null);
  if (featuredImage) {
    featuredImage = {
      ...featuredImage,
      path: getImagePath(featuredImage.path, "medium"),
    };
  }
  const { reviews_count } = useSelector(
    ({ propertyPageData }: AppState) => ({
      reviews_count: propertyPageData?.inventoryReviewData?.length || 0,
    }),
    shallowEqual,
  );

  const updatedImageList = [
    featuredImage,
    ...(item?.images?.filter((image: any) => image?.base_path !== featuredImage?.base_path) || []),
  ];
  const openInventory: MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    gtagEventEmitter({
      action: parentCanonicalName
        ? `see_all_button_clicked`
        : gtagEventAction || "inventory_clicked",
      category: gtagEventCategory || PAGE.SEARCH,
      label: item?.name,
      canonical_name: item?.canonical_name,
    });

    // For whitelabel in iframe, only open in new tab without navigation
    if (isWhitelabel && isIframe) {
      window.open(link, "_blank");
      return;
    }

    const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
    propertyPageDispatcher.updateBasicDetails(item);
    customHistoryPush(history, link);
  };
  const [activePos, setActivePos] = useState(1);

  const handleChangeSlide = (pos: number) => setActivePos(pos);
  let containerClassname = parentCanonicalName
    ? styles.blurCompactInventoryCardParent
    : styles.compactInventoryCardParent;
  if (showDistanceRails) {
    containerClassname = parentCanonicalName
      ? styles.blurCompactInventoryCardParentLarge
      : styles.compactInventoryCardParentLarge;
  }

  const onKeyDown = (e: any) => {
    if (e.key === "Enter") {
      openInventory(e);
    }
  };
  const { t } = useTranslationHook();
  // Detect if device supports touch to disable hover-based navigation
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    const hasTouchSupport =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-ignore
      navigator.msMaxTouchPoints > 0;
    setIsTouchDevice(hasTouchSupport);
  }, []);

  const {
    isSaleOfferAvailableOnInventory,
    inventoryBrandImage,
    backgroundGradient,
    borderColor,
  }: any = useAmberSales({
    inventoryMeta: item?.meta_short || item?.meta,
    inventory: item,
    page: "HOME",
  });
  const { redirection_urls } = useSelector(
    ({ userHistory }: AppState) => ({
      redirection_urls: userHistory.redirection_urls,
    }),
    shallowEqual,
  );
  const isHappyFlow = useIsHappyFlow();
  const isHappyFlowBookForm = useIsHappyFlowBookForm();
  const isPaymentBeforeBookform =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.ENABLE_PAYMENT_BEFORE_BF) &&
    item?.payment_before_bookform &&
    isPaymentActive(item);
  const isApplyNowCTA = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.APPLY_NOW_CTA);
  const getButtonText = () => {
    const inventoryId: string = item.id.toString();
    const { payment_initiated, book_form_completed, book_form_initiated, payment_completed } =
      redirection_urls;

    if (isHappyFlow && isPaymentBeforeBookform) {
      if (payment_completed[inventoryId] && book_form_completed[inventoryId])
        return t("View Bookings");
      if (payment_completed[inventoryId])
        return isApplyNowCTA ? t("Continue to Apply") : t("Continue to Book");
      if (payment_initiated[inventoryId]) return t("Continue to Pay");
      return t("View Rooms");
    }

    if (isHappyFlow && !isPaymentBeforeBookform) {
      if (payment_completed[inventoryId]) return t("View Bookings");
      if (
        payment_initiated[inventoryId] ||
        book_form_completed[inventoryId] ||
        book_form_initiated[inventoryId]
      ) {
        return (
          payment_initiated[inventoryId]?.label ||
          book_form_completed[inventoryId]?.label ||
          (isApplyNowCTA ? t("Continue to Apply") : book_form_initiated[inventoryId]?.label)
        );
      }
    }

    return isHappyFlow ? t("View Rooms") : t("View Property");
  };
  const handleVisitRooms = () => {
    const params = getPropertyFilter();
    const updatedPrams = {
      ref: "home",
      ...params,
    };

    const redirectLink = getLink(item, updatedPrams);

    runTasks([
      () => navigateTo(history, `${redirectLink}#room_type`),
      () => {
        const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
        propertyPageDispatcher.updateBasicDetails(item);
      },
    ]);
  };

  const handleUserFlow = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    const inventoryId: string = item.id.toString();
    const { payment_initiated, book_form_completed, book_form_initiated, payment_completed } =
      redirection_urls;

    if (isHappyFlow && isPaymentBeforeBookform) {
      if (payment_completed[inventoryId] && book_form_completed[inventoryId])
        navigateTo(history, "/profile/leads");
      else if (payment_completed[inventoryId]) {
        const dataObj = book_form_initiated[inventoryId] || payment_completed[inventoryId];
        const redirectLink = `${dataObj.link}&payment_before_bookform=true`;
        if (isHappyFlowBookForm)
          navigateTo(history, link[0] === "/" ? redirectLink : `/${redirectLink}`);
        else history.push(link[0] === "/" ? redirectLink : `/${redirectLink}`);
      } else if (payment_initiated[inventoryId]) {
        const redirectLink = `${payment_initiated[inventoryId].link}&payment_before_bookform=true`;
        if (isHappyFlowBookForm)
          navigateTo(history, link[0] === "/" ? redirectLink : `/${redirectLink}`);
        else history.push(redirectLink[0] === "/" ? redirectLink : `/${redirectLink}`);
      } else handleVisitRooms();
    } else if (!isPaymentBeforeBookform && isHappyFlow) {
      if (payment_completed[inventoryId]) navigateTo(history, "/profile/leads");
      else if (
        payment_initiated[inventoryId] ||
        book_form_completed[inventoryId] ||
        book_form_initiated[inventoryId]
      ) {
        // link will be null incase where payment is completed once
        const redirectLink =
          payment_initiated[inventoryId]?.link ||
          book_form_completed[inventoryId]?.link ||
          book_form_initiated[inventoryId]?.link ||
          "profile/leads";

        if (isHappyFlowBookForm)
          navigateTo(history, link[0] === "/" ? redirectLink : `/${redirectLink}`);
        else history.push(link[0] === "/" ? redirectLink : `/${redirectLink}`);
      } else handleVisitRooms();
    }
  };

  return (
    <div
      className={formatClassNames(
        styles.compactInventoryCardParentOuter,
        isDesktop && styles.desktopOuter,
        className,
      )}
      data-testid={`${dataTestId}-card`}
    >
      <div
        role="button"
        className={formatClassNames(
          containerClassname,
          isDesktop ? styles.compactInventoryCardParentDesktop : "",
        )}
        onKeyDown={onKeyDown}
        onClick={openInventory}
        tabIndex={0}
        style={isSaleOfferAvailableOnInventory ? { border: `1.5px solid ${borderColor}` } : {}}
      >
        <div
          role="none"
          className={formatClassNames(
            styles.carouselContainer,
            isSaleOfferAvailableOnInventory ? styles.carouselContainerSaleOffer : "",
          )}
          onClick={(e) => e.stopPropagation()}
          ref={imageWrapperRef}
        >
          {showIcons && (
            <>
              {/* Hide shortlist button for whitelabel in iframe */}
              {!(isWhitelabel && isIframe) && (
                <ShortlistButton
                  inventoryId={item?.id}
                  dataTestId={`${dataTestId}-card-shortlist-button`}
                  inventory={item}
                  screenName={screenName}
                />
              )}
              <PropertyTags className={styles.propertyTag} inventory={item} />
            </>
          )}
          <Carousel
            slideList={updatedImageList
              .slice(0, Math.min(updatedImageList.length, 5))
              .map((img) => (
                <Image
                  src={getImagePath(img?.base_path, "gallery-image-small")}
                  width="100%"
                  height={isSaleOfferAvailableOnInventory ? "136px" : "160px"}
                  rootMargin="0px 0px 0px 0px"
                  onClick={openInventory}
                  data-testid="inventorycard-propertyImage"
                  className={styles.imageItem}
                />
              ))}
            activePos={activePos}
            handleChangeSlide={handleChangeSlide}
            className={styles.carousel}
            showNavOnHover={!isTouchDevice}
            {...(isDesktop ? { navSliderColor: "transparent" } : {})}
            showNavSlider={isDesktop}
          />
          <CarouselIndicator
            activeDot={activePos}
            totalSlider={updatedImageList.length}
            handleClickIndicator={handleChangeSlide}
            indicatorWrapperClassName={styles.indicator}
            type="line"
            dotColor="white"
            addInfinite
            maxDots={3}
          />
        </div>

        {isSaleOfferAvailableOnInventory && (
          <div
            className={styles.saleOfferContainer}
            style={{
              background: backgroundGradient,
            }}
          >
            <Image
              src={inventoryBrandImage}
              alt="right-section-desktop"
              width={110}
              height="auto"
              isEagerLoad
            />
          </div>
        )}
        <div className={styles.content}>
          <CustomLink
            stopLink
            className={styles.title}
            href={link}
            onClick={(e: any) => openInventory(e)}
          >
            <h4
              className={formatClassNames(isWhitelabel ? "wl-title-font" : "", styles.title)}
              data-testid={`${dataTestId}-section-card-inventoryName`}
            >
              {item?.name}
            </h4>
            <div className={styles.subtitle} data-testid={`${dataTestId}-card-inventoryAddress`}>
              {location}
            </div>
          </CustomLink>
          <div className={styles.bottomContainer}>
            <div className={styles.priceContainer}>
              <div>{config.IS_CHINA_APP ? "" : t("propertypage.compactinventorycard.from")}</div>
              <span data-testid={`${dataTestId}-compactCard-inventoryPrice`}>{pricingPrice}/</span>
              {t(pricingDuration)}
            </div>
            {reviews_count ? (
              <TrustPilotBadge
                reviews_count={reviews_count}
                reviews_rating={item?.reviews_rating}
                className={styles.trustPilotBadge}
                dataTestId="card-trustPilot-tag"
              />
            ) : (
              <div className={styles.trustPilotBadge} />
            )}
          </div>
          {showHappyFlowCTA && (
            <Button
              variant="primary"
              onClick={handleUserFlow}
              {...(!isDesktop && { className: styles.buttonCTA })}
            >
              {getButtonText()}
            </Button>
          )}
        </div>
        {showDistanceRails && item?.top_meta_distances && (
          <div className={styles.horizontalScroll}>
            {item?.top_meta_distances
              .filter(
                (obj: any, index: number, self: any) =>
                  index === self.findIndex((o: any) => o.place === obj.place),
              )
              .map((md: any) => (
                <DistanceScroll data={md} />
              ))}
          </div>
        )}
      </div>
      {parentCanonicalName && (
        <div
          onClick={openInventory}
          className={styles.blurOverlay}
          role="button"
          onKeyDown={(event: any) => {
            if (event.key === "Enter") {
              openInventory(event);
            }
          }}
          tabIndex={0}
        >
          <div className={styles.seeAllBtn} data-testid={`${dataTestId}-card-seeAll-button`}>
            {t("propertypage.recentelyviewedsection.seeall")}
            <div className={styles.chevron}>
              <Chevron height="12" width="12" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(CompactInventoryCard, {
  componentName: "CompactInventoryCard",
  showForChina: true,
});
